/**
 * `useSwitchOrganization` — switches the user's active organization.
 *
 * Tells the API to re-scope the session's WorkOS token to the target org
 * (rotating the session cookie) and persists the selection atomically, then
 * refreshes `useMe`.
 */

import {
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import { useAppAuth } from "./use-auth";

import { api } from "../lib/api-client";

// Shared key so every instance of this hook (header + dashboard OrgSwitcher)
// maps to the same TanStack mutation — and so the pending state below can be
// observed across all of them.
const SWITCH_ORGANIZATION_MUTATION_KEY = ["switch-organization"] as const;

export function useSwitchOrganization() {
  const { user } = useAppAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [...SWITCH_ORGANIZATION_MUTATION_KEY],
    mutationFn: async (organizationId: string) => {
      if (!user) throw new Error("not authenticated");
      // The API re-scopes the session's WorkOS token to the target org
      // (rotating the session cookie) and persists the selection atomically.
      return await api.switchOrganization(organizationId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me", user?.id] });
    },
  });

  // There can be two OrgSwitcher instances mounted at once (header +
  // dashboard); read the pending state across ALL mutations with the shared
  // key so a switch started in one disables the other too.
  const pendingSwitches = useMutationState({
    filters: {
      mutationKey: [...SWITCH_ORGANIZATION_MUTATION_KEY],
      status: "pending",
    },
  });

  // Same shape as the `useMutation` result — only `isPending` is widened to
  // reflect every instance of the mutation, not this hook's own.
  return { ...mutation, isPending: pendingSwitches.length > 0 };
}
